import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Euro, Info } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface JornadaTarget {
  id: string;
  tecnico_id: string;
  tecnico_nombre?: string;
  fecha: string;
  horas_trabajadas: number | null;
  es_hora_extra: boolean;
  coste_hora_aplicado: number | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jornada: JornadaTarget | null;
  onSaved: () => void;
}

const fmtEur = (v: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

export const AsignarCosteJornadaDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  jornada,
  onSaved,
}) => {
  const { perfil } = useAuth();
  const { toast } = useToast();
  const [coste, setCoste] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && jornada) {
      setCoste(jornada.coste_hora_aplicado != null ? String(jornada.coste_hora_aplicado) : "");
    }
  }, [open, jornada]);

  if (!jornada) return null;

  const costeNum = parseFloat(coste);
  const valido = !isNaN(costeNum) && costeNum >= 0;
  const horas = Number(jornada.horas_trabajadas) || 0;
  const importe = valido ? costeNum * horas : 0;

  const guardar = async (crearTarifa: boolean) => {
    if (!valido) {
      toast({ title: "Coste inválido", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      // 1) Actualizar la jornada — el trigger respetará coste_hora_aplicado y recalculará coste_total
      const { error: errJ } = await supabase
        .from("obra_jornadas")
        .update({ coste_hora_aplicado: costeNum })
        .eq("id", jornada.id);
      if (errJ) throw errJ;

      // 2) Opcional: crear tarifa para el técnico
      if (crearTarifa) {
        if (!perfil?.empresa_id) throw new Error("Sin empresa");
        const { error: errT } = await supabase.from("tecnico_tarifas" as any).insert({
          tecnico_id: jornada.tecnico_id,
          empresa_id: perfil.empresa_id,
          coste_hora: costeNum,
          coste_hora_extra: costeNum,
          vigente_desde: jornada.fecha,
        });
        if (errT) throw errT;
      }

      toast({
        title: "Coste asignado",
        description: crearTarifa
          ? "Jornada actualizada y tarifa del técnico creada."
          : "Coste aplicado solo a esta jornada.",
      });
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Euro className="h-5 w-5" />
            Asignar coste a jornada
          </DialogTitle>
          <DialogDescription>
            El coste introducido se congela en esta jornada y no se verá afectado por cambios futuros de tarifa.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
          <div className="font-medium">{jornada.tecnico_nombre || "Técnico"}</div>
          <div className="text-muted-foreground text-xs">
            {format(new Date(jornada.fecha), "dd MMM yyyy", { locale: es })} · {horas.toFixed(2)}h
            {jornada.es_hora_extra && (
              <Badge variant="outline" className="ml-2 bg-orange-500/10 text-orange-600 border-orange-500/40">
                Extra
              </Badge>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="coste-hora">Coste / hora aplicado (€)</Label>
          <Input
            id="coste-hora"
            type="number"
            step="0.01"
            min="0"
            value={coste}
            onChange={(e) => setCoste(e.target.value)}
            placeholder="ej. 25.00"
            autoFocus
          />
          {valido && horas > 0 && (
            <div className="text-sm text-muted-foreground">
              Importe total: <strong className="text-foreground">{fmtEur(importe)}</strong>
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-xs">
          <Info className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" />
          <span>
            <strong>"Aplicar y crear tarifa del técnico"</strong> guardará además este coste como
            tarifa vigente del técnico desde la fecha de la jornada, para que las próximas jornadas
            lo apliquen automáticamente. No afecta al resto de jornadas pasadas.
          </span>
        </div>

        <DialogFooter className="gap-2 sm:flex-col sm:items-stretch">
          <Button
            onClick={() => guardar(true)}
            disabled={!valido || saving}
            className="w-full"
          >
            Aplicar y crear tarifa del técnico
          </Button>
          <Button
            variant="outline"
            onClick={() => guardar(false)}
            disabled={!valido || saving}
            className="w-full"
          >
            Solo esta jornada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
